import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function NewBookModal({ onClose }) {
  const navigate = useNavigate();
  const backdropRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  // Smooth entrance — trigger animation shortly after mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Smooth close — play exit animation, then unmount
  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 450);
  };

  return (
    <div
      ref={backdropRef}
      onClick={(e) => {
        if (e.target === backdropRef.current) handleClose();
      }}
      className="fixed inset-0 flex items-center justify-center z-[100] p-3 md:p-6 font-libre"
      style={{
        backgroundColor: isVisible ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0)',
        backdropFilter: isVisible ? 'blur(4px)' : 'blur(0px)',
        transition: 'background-color 0.5s ease, backdrop-filter 0.5s ease',
      }}
    >
      <div
        className="relative flex flex-col md:flex-row w-full max-w-[90%] md:max-w-5xl bg-[#fdfbf7] rounded-lg md:rounded-sm overflow-hidden max-h-[90vh] md:max-h-auto overflow-y-auto md:overflow-visible"
        style={{
          boxShadow: "0 20px 50px rgba(0,0,0,0.5), inset 0 0 100px rgba(0,0,0,0.1)",
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(40px)',
          transition: 'opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1), transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >

        {/* === LEFT PAGE (IMAGE / COVER) === */}
        <div className="w-full md:w-1/2 relative bg-[#1a2e28] p-0 flex items-center justify-center border-b md:border-b-0 md:border-r border-[#e0dcd0] min-h-[200px] md:min-h-full overflow-hidden">

          {/* Inner shadow */}
          <div className="absolute top-0 bottom-0 right-0 w-12 bg-gradient-to-l from-black/30 to-transparent pointer-events-none z-10 hidden md:block"></div>

          {/* Book Image Presentation - Full Cover */}
          <div className="relative z-0 w-full h-full">
            <img
              src="/assets/allBooks.jpeg"
              alt="New Book Releases"
              className="w-full h-full object-cover md:object-cover hover:scale-105 transition-transform duration-700 ease-in-out"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none"></div>
          </div>
        </div>

        {/* === RIGHT PAGE (CONTENT) === */}
        <div className="w-full md:w-1/2 relative p-6 md:p-12 flex flex-col justify-center text-center md:text-left bg-[#fdfbf7]">
          {/* Inner shadow */}
          <div className="absolute top-0 bottom-0 left-0 w-12 bg-gradient-to-r from-black/10 to-transparent pointer-events-none z-10 hidden md:block"></div>

          {/* Paper Texture */}
          <div className="absolute inset-0 opacity-20 pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')]"></div>

          <div className="relative z-20">
            <h2 className="text-2xl md:text-4xl font-bold text-[#3e2723] mb-4 md:mb-6 font-libre drop-shadow-sm text-center">
              السلام عليكم
            </h2>

            <div className="mb-6 md:mb-8 relative px-2">
              <span className="absolute -left-2 -top-2 text-4xl md:text-6xl text-[#5d4037] opacity-10 font-serif">"</span>
              <p className="text-[#4e342e] text-base md:text-xl leading-relaxed text-center font-medium md:py-4 md:px-6">

                It's possible to <span className="font-extrabold text-[#5d4037]">read</span> and <span className="font-extrabold text-[#5d4037]">download</span> the newly published books and magazines under the Darul Hadith Research Center
              </p>
              <span className="absolute -right-2 -bottom-2 text-4xl md:text-6xl text-[#5d4037] opacity-10 font-serif rotate-180">"</span>
            </div>

            <div className="flex flex-row items-center justify-center gap-3 md:gap-4 mt-2">
              <button
                onClick={handleClose}
                className="px-4 md:px-8 py-2 md:py-3 text-sm md:text-base text-[#5d4037] font-bold border-2 border-[#5d4037]/20 rounded-lg hover:bg-[#5d4037]/5 hover:border-[#5d4037]/40 transition-all duration-300 w-full md:w-auto"
              >
                Not Now
              </button>

              <button
                onClick={() => {
                  navigate('/library');
                  handleClose();
                }}
                className="px-4 md:px-8 py-2 md:py-3 text-sm md:text-base bg-[#5d4037] text-[#fdfbf7] font-bold rounded-lg shadow-lg hover:bg-[#3e2723] hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 w-full md:w-auto flex items-center justify-center gap-2"
              >
                <span>Read Books</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 md:w-5 md:h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </button>
            </div>

            <p className="text-center text-[10px] md:text-xs text-[#8d6e63] mt-6 md:mt-8 uppercase tracking-widest opacity-60">
              Official Publication of Darul Hadees
            </p>
          </div>
        </div>

        {/* Center Spine Divider */}
        <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-[1px] bg-[#d7ccc8] z-30 opacity-50"></div>
      </div>
    </div>
  );
}
